import { serverSideRedirect } from "@/utils/server/serverSide";
import { Tabs } from "antd";
import classNames from "classnames";
import Head from "next/head";
import Link from "next/link";
import router, { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";

import SearchMyBoardList from "@/pageComponents/SearchMyTab/SearchMyBoardList";
import SearchMyPinList from "@/pageComponents/SearchMyTab/SearchMyPinList";
import SearchBoardPinList from "../../pageComponents/SearchBoardPinList";
import SearchDomainPinList from "../../pageComponents/SearchDomainPinList";

import HBIcon from "@/components/HBIcon";

import { SORT, SORT_BY_SEARCH } from "@/constants/sort";

import HBAvatar from "@/components/Avatar";
import useAppStore from "@/context/store";
import Search from "@/pageComponents/Search";
import SearchEmpty from "@/pageComponents/Search/SearchEmpty";
import SearchAIPinList from "@/pageComponents/SearchAIPinList";
import SearchFollowingBoardList from "@/pageComponents/SearchFollowingBoardList";
import SearchFollowingUserList from "@/pageComponents/SearchFollowingUser";
import SearchTopicPinList from "@/pageComponents/SearchTopicPinList";
import {
  getAiFilter,
  getBoard,
  getDomain,
  getSearchMaterialFilter,
  getSearchPinsFilter,
  getTopicDetail,
  getUser,
} from "@/services";
import { setSearch } from "@/tracker/shared/global";
import { filterFiltersByUser } from "@/utils/getFilters";
import getTopicInfo from "@/utils/getTopicInfo";
import styles from "./index.module.less";
const TabPane = Tabs.TabPane;

function SearchUser(props) {
  let { q, subtype, urlname } = props;
  const { headerHeight: height } = useAppStore(({ headerHeight }) => ({
    headerHeight,
  }));

  const [user, setUser] = useState(null);

  if (!subtype) {
    subtype = "pin";
  }

  const queryAction = useMemo(() => {
    return async () => {
      if (!urlname) {
        return;
      }

      const result = await getUser({ urlname });
      const user = result.user || null;
      setUser(user);
    };
  }, [urlname]);

  useEffect(() => {
    queryAction();
  }, [queryAction]);

  const onChangeTab = (subtype) => {
    setSearch({
      search_type: "筛选器-点击",
    });

    router.push({
      pathname: "/search",
      query: { subtype, q, urlname },
    });
  };

  const onBack = () => {
    if (window.history.length > 1) {
      router.back();
    } else {
      router.push(`/user/${user?.urlname}`);
    }
  };

  return (
    <div
      className={classNames(
        styles.search,
        styles.searchOther,
        "calculateWrapper"
      )}
    >
      <Head>
        <title>{q} - 搜索结果 - 花瓣</title>
      </Head>

      <div className={styles.user} style={{ top: `${height}px` }}>
        {/* 返回 上一页 或 用户主页 */}
        <div className={styles.back} onClick={onBack}>
          <HBIcon className={styles.icon} type="ic_arrow_upward" />
        </div>

        {/* 用户头像 */}
        <Link target="_blank" href={`/user/${user?.urlname}`}>
          <a className={styles.avatar}>
            <HBAvatar user={user} size={32} shape="circle" />
            <span className={styles.name}>{user?.username || ""}</span>
          </a>
        </Link>
      </div>

      <div
        className={classNames(styles.border, "calculateWrapper")}
        style={{
          top: `${54 + height}px`,
        }}
      />

      <Tabs
        activeKey={subtype}
        destroyInactiveTabPane={true}
        className={"calculateWrapper"}
        tabBarStyle={{ top: `${height}px` }}
        onChange={onChangeTab}
        centered
      >
        <TabPane tab="采集" key="pin">
          <SearchMyPinList query={props} empty={<SearchEmpty />} />
        </TabPane>
        <TabPane tab="画板" key="board">
          <SearchMyBoardList query={props} empty={<SearchEmpty />} />
        </TabPane>
      </Tabs>
    </div>
  );
}

function SearchAi({ q, filterIds, sort, serverSideAiFilters, rootId }) {
  const { headerHeight: height } = useAppStore(({ headerHeight }) => ({
    headerHeight,
  }));

  return (
    <div
      className={classNames(styles.search, "calculateWrapper")}
      style={{ paddingTop: 70, margin: "auto" }}
    >
      <Head>
        <title>{q} - AI圈搜索结果 - 花瓣</title>
      </Head>

      <div
        className={classNames(styles.board, "calculateWrapper")}
        style={{ top: `${height}px` }}
      >
        <Link href={`/ai`}>
          <a className={styles.img}>
            <HBIcon className={styles.icon} type="ic_arrow_upward" />
            <span className={styles.name}>{"AI圈"}</span>
          </a>
        </Link>
      </div>

      <div
        className={classNames(styles.border, "calculateWrapper")}
        style={{
          top: `${54 + height}px`,
        }}
      />
      <SearchAIPinList
        query={{ q, filterIds, sort }}
        empty={<SearchEmpty />}
        serverSideAiFilters={serverSideAiFilters}
        rootId={rootId}
      />
    </div>
  );
}

function SearchBoard(props) {
  let { q, boardId } = props;

  const { headerHeight: height, wrapperWidth } = useAppStore(
    ({ headerHeight, wrapperWidth }) => ({
      headerHeight,
      wrapperWidth,
    })
  );
  const [board, setBoard] = useState(null);

  const queryAction = useMemo(() => {
    return async () => {
      if (!boardId) {
        return;
      }

      const result = await getBoard({ board_id: boardId });
      const board = result.board || null;
      setBoard(board);
    };
  }, [boardId]);

  useEffect(() => {
    queryAction();
  }, [queryAction]);

  const query = {
    userId: board?.user_id,
    ...props,
  };

  return (
    <div
      className={classNames(
        styles.search,
        styles.searchOther,
        "calculateWrapper"
      )}
    >
      <Head>
        <title>{q} - 搜索结果 - 花瓣</title>
      </Head>

      <div
        className={classNames(styles.board, "calculateWrapper")}
        style={{ top: `${height}px`, width: wrapperWidth }}
      >
        <Link target="_blank" href={`/boards/${board?.board_id}`}>
          <a className={styles.img}>
            <HBIcon className={styles.icon} type="ic_arrow_upward" />
            <HBIcon className={styles.logo} type="dashboard" />
            <span className={styles.name}>{board?.title || ""}</span>
          </a>
        </Link>
      </div>

      <div
        className={classNames(styles.border, "calculateWrapper")}
        style={{
          // width: `${width}px`,
          top: `${54 + height}px`,
        }}
      />
      <SearchBoardPinList query={query} empty={<SearchEmpty />} />
    </div>
  );
}

function SearchDomain(props) {
  let { q, domainId } = props;

  const { headerHeight: height } = useAppStore(({ headerHeight }) => ({
    headerHeight,
  }));

  const [domain, setDomain] = useState(null);

  const queryAction = useMemo(() => {
    return async () => {
      if (!domainId) {
        return;
      }

      const result = await getDomain({ domain_id: domainId });
      const domain = result || null;
      setDomain(domain);
    };
  }, [domainId]);

  useEffect(() => {
    queryAction();
  }, [queryAction]);

  const query = {
    ...props,
  };

  return (
    <div
      className={classNames(
        styles.search,
        styles.searchOther,
        "calculateWrapper"
      )}
    >
      <Head>
        <title>{q} - 搜索结果 - 花瓣</title>
      </Head>

      <div
        className={classNames(styles.domain, "calculateWrapper")}
        style={{ top: `${height}px` }}
      >
        <Link target="_blank" href={`/domains/${domain?.domain_id}`}>
          <a className={styles.img}>
            <HBIcon className={styles.icon} type="ic_arrow_upward" />
            <HBIcon className={styles.logo} type="link" />
            <span className={styles.name}>{domain?.domain_name || ""}</span>
          </a>
        </Link>
      </div>

      <div
        className={classNames(styles.border, "calculateWrapper")}
        style={{
          // width: `${width}px`,
          top: `${54 + height}px`,
        }}
      />
      <SearchDomainPinList query={query} empty={<SearchEmpty />} />
    </div>
  );
}

function SearchTopic(props) {
  let { q, urlname, filterId } = props;

  const { headerHeight: height } = useAppStore(({ headerHeight }) => ({
    headerHeight,
  }));

  const [topic, setTopic] = useState(null);

  const queryAction = useMemo(() => {
    return async () => {
      if (!urlname) {
        return;
      }

      const result = await getTopicDetail({ urlname: urlname });
      const topicInfo = getTopicInfo(result, filterId);
      setTopic(topicInfo);
    };
  }, [urlname, filterId]);

  useEffect(() => {
    queryAction();
  }, [queryAction]);

  const query = {
    ...props,
  };

  return (
    <div
      className={classNames(
        styles.search,
        styles.searchOther,
        "calculateWrapper"
      )}
    >
      <Head>
        <title>{q} - 搜索结果 - 花瓣</title>
      </Head>

      <div
        className={classNames(styles.domain, "calculateWrapper")}
        style={{ top: `${height}px` }}
      >
        <Link
          target="_blank"
          href={`/topics/${topic?.urlname}-${filterId || ""}`}
        >
          <a className={styles.img}>
            <HBIcon className={styles.icon} type="ic_arrow_upward" />
            <HBIcon className={styles.logo} type="ic_tag" />
            <span className={styles.name}>{topic?.title || ""}</span>
          </a>
        </Link>
      </div>

      <div
        className={classNames(styles.border, "calculateWrapper")}
        style={{
          // width: `${width}px`,
          top: `${54 + height}px`,
        }}
      />
      <SearchTopicPinList query={query} empty={<SearchEmpty />} />
    </div>
  );
}

function SearchFollowingBoard(props) {
  let { q, urlname } = props;

  const { headerHeight: height, user: currentUser } = useAppStore(
    ({ headerHeight, user }) => ({
      headerHeight,
      user,
    })
  );

  const isMe = useMemo(() => {
    return urlname === currentUser?.urlname;
  }, [urlname, currentUser]);

  const [user, setUser] = useState(null);

  const queryAction = useMemo(() => {
    return async () => {
      if (!urlname || isMe) {
        return;
      }

      const result = await getUser({ urlname });
      const user = result.user || null;
      setUser(user);
    };
  }, [urlname, isMe]);

  useEffect(() => {
    queryAction();
  }, [queryAction]);

  const query = {
    urlname,
    ...props,
  };

  return (
    <div
      className={classNames(
        styles.search,
        styles.searchOther,
        "calculateWrapper"
      )}
    >
      <Head>
        <title>{q} - 搜索结果 - 花瓣</title>
      </Head>

      <div
        className={classNames(styles.board, "calculateWrapper")}
        style={{ top: `${height}px` }}
      >
        <Link target="_blank" href={`/user/${urlname}/following/board`}>
          <a className={styles.img}>
            <HBIcon className={styles.icon} type="ic_arrow_upward" />
            <span className={styles.name}>{`${
              isMe ? "我" : user?.username || "Ta"
            }的关注`}</span>
          </a>
        </Link>
      </div>

      <div
        className={classNames(styles.border, "calculateWrapper")}
        style={{
          // width: `${width}px`,
          top: `${54 + height}px`,
        }}
      />
      <SearchFollowingBoardList query={query} empty={<SearchEmpty />} />
    </div>
  );
}

function SearchFollowingUser(props) {
  let { q, urlname } = props;

  const { headerHeight: height, user: currentUser } = useAppStore(
    ({ headerHeight, user }) => ({
      headerHeight,
      user,
    })
  );

  const isMe = useMemo(() => {
    return urlname === currentUser?.urlname;
  }, [urlname, currentUser]);

  const [user, setUser] = useState(null);

  const queryAction = useMemo(() => {
    return async () => {
      if (!urlname || isMe) {
        return;
      }

      const result = await getUser({ urlname });
      const user = result.user || null;
      setUser(user);
    };
  }, [urlname, isMe]);

  useEffect(() => {
    queryAction();
  }, [queryAction]);

  const query = {
    urlname,
    ...props,
  };

  return (
    <div
      className={classNames(
        styles.search,
        styles.searchOther,
        "calculateWrapper"
      )}
    >
      <Head>
        <title>{q} - 搜索结果 - 花瓣</title>
      </Head>

      <div
        className={classNames(styles.board, "calculateWrapper")}
        style={{ top: `${height}px` }}
      >
        <Link target="_blank" href={`/user/${urlname}/following/user`}>
          <a className={styles.img}>
            <HBIcon className={styles.icon} type="ic_arrow_upward" />
            <span className={styles.name}>{`${
              isMe ? "我" : user?.username || "Ta"
            }的关注`}</span>
          </a>
        </Link>
      </div>

      <div
        className={classNames(styles.border, "calculateWrapper")}
        style={{
          // width: `${width}px`,
          top: `${54 + height}px`,
        }}
      />
      <SearchFollowingUserList query={query} empty={<SearchEmpty />} />
    </div>
  );
}

export async function getServerSideProps({ query, path, res }) {
  const { type, q } = query;
  if (!q && path === undefined) {
    return serverSideRedirect(res, {
      destination: "/",
      permanent: false,
    });
  }

  let pinFilters = [];
  let materialFilters = [];
  let aiFilters = [];
  let rootId = "";
  if ((!type && !path) || type === "pin" || path === "topic-pin") {
    pinFilters = await getSearchPinsFilter();
  } else if (type === "material" || path === "topic-material") {
    materialFilters = await getSearchMaterialFilter();
  } else if (type === "aigc") {
    let aiResult = await getAiFilter();
    aiFilters = aiResult.aiFilters;
    rootId = aiResult.rootId;
  }

  return {
    props: {
      serverSidePinFilters: pinFilters,
      serverSideMaterialFilters: materialFilters,
      serverSideAiFilters: aiFilters,
      rootId,
    },
  };
}

export default function SearchPage(props) {
  const user = useAppStore((s) => s.user);
  const router = useRouter();
  let { query } = router;
  if (props.query) {
    query = props.query;
  }

  // 添加和移除 search-page-content 样式类
  useEffect(() => {
    const layoutContent = document.getElementById("layout-content");
    if (layoutContent) {
      layoutContent.classList.add("search-page-content");
    }

    // 组件卸载时移除样式类
    return () => {
      if (layoutContent) {
        layoutContent.classList.remove("search-page-content");
      }
    };
  }, []);

  if (!router.isReady) {
    return null;
  }

  const {
    type = "pin", // 搜索类型: pin, board, user, my, following
    subtype = "board",
    category = null, // 采集类型
    q = null, // 关键字
    sort = null,
    file_type = null,
    urlname = null,
    color = null,
    board_id = null,
    domain_id = null,
    material = null,
    ratio = null,
    resolution = null,
    filter_ids = null,
    filter_id = null,
    topic = null,
  } = query;

  if (urlname) {
    if (type === "following") {
      if (subtype === "board") {
        return <SearchFollowingBoard q={q} urlname={urlname} />;
      } else if (subtype === "user") {
        return <SearchFollowingUser q={q} urlname={urlname} />;
      }
    } else {
      return (
        <SearchUser
          q={q}
          urlname={urlname}
          subtype={subtype}
          searchResult={props?.searchResult}
        />
      );
    }
  } else if (type === "aigc") {
    return (
      <SearchAi
        q={q}
        filterIds={filter_ids}
        sort={sort || SORT.ALL}
        serverSideAiFilters={filterFiltersByUser(
          props.serverSideAiFilters,
          user
        )}
        rootId={props.rootId}
      />
    );
  } else if (board_id) {
    return (
      <SearchBoard
        q={q}
        boardId={board_id}
        fileType={file_type}
        color={color}
        sort={sort || SORT_BY_SEARCH.ALL}
        resolution={resolution}
      />
    );
  } else if (domain_id) {
    return <SearchDomain q={q} domainId={domain_id} />;
  } else if (topic) {
    return <SearchTopic q={q} urlname={topic} filterId={filter_id} />;
  } else {
    return (
      <Search
        category={category}
        type={type}
        subtype={subtype}
        q={q}
        sort={sort || SORT.ALL}
        fileType={file_type}
        color={color}
        ratio={ratio}
        resolution={resolution}
        material={material}
        filterIds={filter_ids}
        searchResult={props?.searchResult}
        serverSideFacets={props?.serverSideFacets}
        serverSidePinFilters={filterFiltersByUser(
          props?.serverSidePinFilters,
          user
        )}
        serverSideMaterialFilters={filterFiltersByUser(
          props?.serverSideMaterialFilters,
          user
        )}
        pageInfo={props?.pageInfo}
      />
    );
  }
}
